import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Phone, MessageSquare } from "lucide-react";
import { PHONE, PHONE_TEL } from "../data/content";
import { trackEvent } from "../lib/analytics";

/**
 * Site-wide sticky mobile call bar.
 *
 * Why: 70%+ of Tech Emergency's traffic is mobile + local-intent. The most
 * valuable conversion is a phone tap. We make a tap unmissable on every page,
 * and we record WHICH page produced the tap so admin can see what's working.
 *
 * Hidden on:
 *   - /admin/*    (admin doesn't need to call himself)
 *   - /portal/*   (logged-in customers shouldn't get a hard CTA)
 *   - print pages (flyers)
 */
const HIDE_PREFIXES = ["/admin", "/portal", "/flyers"];

function pageTag(pathname) {
  // Compact, URL-safe descriptor of where the tap came from. Stored as a query
  // param on the tel: link AND beaconed to /api/track/phone-tap so we can group
  // taps by page in the admin Call Sources panel.
  if (pathname === "/") return "home";
  return pathname.replace(/^\/+/, "").replace(/\/+/g, "_").slice(0, 60) || "home";
}

export default function StickyCallBar() {
  const { pathname } = useLocation();
  const [shown, setShown] = useState(false);

  useEffect(() => {
    // Show after a short delay so the bar doesn't fight with the hero on first
    // paint. ~600ms feels less aggressive than instant.
    const t = setTimeout(() => setShown(true), 600);
    return () => clearTimeout(t);
  }, []);

  if (HIDE_PREFIXES.some((p) => pathname.startsWith(p))) return null;

  const src = pageTag(pathname);

  const handleCallClick = () => {
    trackEvent("phone_call_click", { location: "sticky_bar", path: pathname });
    // Fire-and-forget tracking beacon. Uses sendBeacon when available so the
    // browser doesn't cancel it when the dialer opens. Falls back to fetch.
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/track/phone-tap`;
      const body = JSON.stringify({ src, path: pathname, ua: navigator.userAgent });
      if (navigator.sendBeacon) {
        navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
      } else {
        fetch(url, { method: "POST", body, headers: { "Content-Type": "application/json" }, keepalive: true });
      }
    } catch { /* never block the tap on tracking failure */ }
  };

  return (
    <>
      {/* Mobile-only — desktop already shows a prominent Call button in the header. */}
      <div
        className="md:hidden fixed bottom-0 left-0 right-0 z-40 transition-transform duration-300"
        style={{
          transform: shown ? "translateY(0)" : "translateY(100%)",
          background: "var(--te-bg)",
          borderTop: "1px solid var(--te-border)",
          boxShadow: "0 -8px 24px rgba(0,0,0,0.4)",
        }}
        data-testid="sticky-call-bar"
      >
        <div className="grid grid-cols-5 gap-0 h-14">
          <a
            href={`/book?src=${src}`}
            data-testid="sticky-cta-book"
            className="col-span-2 flex items-center justify-center text-sm font-semibold tracking-tight"
            style={{ background: "transparent", color: "var(--te-text)" }}
          >
            <MessageSquare size={14} className="mr-1.5" /> Send an enquiry
          </a>
          <a
            href={`${PHONE_TEL}?src=${src}`}
            onClick={handleCallClick}
            data-testid="sticky-cta-call"
            className="col-span-3 flex items-center justify-center text-sm font-bold tracking-tight"
            style={{ background: "var(--te-primary)", color: "#fff" }}
          >
            <Phone size={15} className="mr-2" />
            Call {PHONE}
          </a>
        </div>
      </div>

      {/* Push content up so the sticky bar doesn't cover footer links on mobile. */}
      <div className="md:hidden" aria-hidden="true" style={{ height: 56 }} />
    </>
  );
}
